import React from "react";
import Modal from "../../../shared/ui/Modal";
import { ActivationFailureItem, updateItems } from "../items.api";
import { fetchProcessReadiness } from "../../../api/account";
import { AuthContextType } from "../../auth/AuthContext";
import {
    buildBatchPayload,
    ITEM_BATCH_ACTION_LABELS,
    ItemBatchActionType,
} from "../model/item-batch.types";

interface ItemBatchActionModalProps {
    auth: AuthContextType;
    show: boolean;
    actionType: ItemBatchActionType | '';
    itemGroups: any[];
    selectedItems: string[];
    onHide: () => void;
    onComplete: () => void;
    onFocusItem: (itemId: string) => void;
}

type BatchPhase = 'form' | 'processing' | 'done';

const INITIAL_FORM = {
    selectedGroup: "",
    newPrice: "",
    newDiscount: "",
    priceBasis: "",
    adjustStrategy: "",
};

export const ItemBatchActionModal: React.FC<ItemBatchActionModalProps> = (props) => {
    const [phase, setPhase] = React.useState<BatchPhase>('form');
    const [errorMessage, setErrorMessage] = React.useState("");
    const [activationFailures, setActivationFailures] = React.useState<ActivationFailureItem[]>([]);
    const [resultMessage, setResultMessage] = React.useState("");
    const [form, setForm] = React.useState(INITIAL_FORM);

    const title = props.actionType ? ITEM_BATCH_ACTION_LABELS[props.actionType] : '';
    const payload = buildBatchPayload(props.actionType, form);
    const isSubmitDisabled = phase !== 'form' || !payload;

    const handleSubmit = async () => {
        if (!payload || phase !== 'form') return;

        setPhase('processing');
        setErrorMessage("");
        setActivationFailures([]);
        setResultMessage("");

        try {
            if (props.actionType === 'instant_process' || props.actionType === 'instant_process_dry_run') {
                const readiness = await fetchProcessReadiness();
                if (!readiness?.ready) {
                    setErrorMessage(
                        readiness?.errors[0]?.message
                        || "죄송합니다. 사용자 전용 환경을 준비 중입니다. 관리자에게 문의해주세요."
                    );
                    setPhase('form');
                    return;
                }
            }

            await updateItems({
                auth: props.auth,
                ids: props.selectedItems,
                payload,
                setErrorMessage: (error: string) => {
                    setErrorMessage(error);
                    setPhase('form');
                },
                setActivationFailures,
                setResult: (message?: string) => {
                    setResultMessage(
                        message || `${props.selectedItems.length}개 상품이 변경되었습니다.`
                    );
                    setPhase('done');
                },
            });
        } catch (error) {
            setErrorMessage('배치 작업 중 오류가 발생했습니다.');
            setPhase('form');
            console.error('Batch update failed:', error);
        }
    };

    const handleConfirmClose = () => {
        props.onComplete();
    };

    const handleModalHide = () => {
        if (phase === 'processing') return;
        if (phase === 'done') {
            handleConfirmClose();
            return;
        }
        props.onHide();
    };

    React.useEffect(() => {
        if (props.show) {
            setPhase('form');
            setForm(INITIAL_FORM);
            setErrorMessage("");
            setActivationFailures([]);
            setResultMessage("");
        }
    }, [props.show, props.actionType]);

    return (
        <Modal
            show={props.show}
            onHide={handleModalHide}
            backdrop="static"
            size="lg"
            scrollable={true}
        >
            <Modal.Header closeButton={phase !== 'processing'}>
                <Modal.Title className="fs-5">{title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {phase === 'processing' && (
                    <div className="alert alert-info" role="alert">
                        배치 작업을 수행 중입니다...
                    </div>
                )}

                {phase === 'done' && resultMessage && (
                    <div className="alert alert-success" role="alert">
                        {resultMessage}
                    </div>
                )}

                {errorMessage && (
                    <div className="alert alert-danger" role="alert">
                        {errorMessage}
                    </div>
                )}

                {activationFailures.length > 0 && (
                    <div className="mb-3">
                        <div className="fw-semibold mb-2">활성화 실패 상품 {activationFailures.length}개</div>
                        <div className="table-responsive">
                            <table className="table table-sm align-middle mb-0">
                                <thead><tr><th scope="col">상품코드</th><th scope="col">사유</th></tr></thead>
                                <tbody>
                                    {activationFailures.map((item) => (
                                        <tr key={item.id}>
                                            <td>
                                                <button type="button" className="btn btn-link p-0 text-start" onClick={() => props.onFocusItem(item.id)}>
                                                    {item.shop_item_id || '상품코드 없음'}
                                                </button>
                                            </td>
                                            <td>{Object.values(item.errors).join(' / ')}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {phase === 'form' && (
                    <>
                        <p>{props.selectedItems.length}개 아이템을 선택했습니다.</p>

                        {props.actionType === 'instant_process' && (
                            <div className="alert alert-warning" role="alert">
                                <strong>주의:</strong> 선택한 아이템이 즉시 처리됩니다. 계속하시겠습니까?
                            </div>
                        )}
                        {props.actionType === 'request_urgent_enqueue' && (
                            <div className="alert alert-info" role="alert">
                                선택한 상품을 우선 실행 대상으로 지정합니다. 작업이 중지된 경우 시작한 뒤 스케줄에서 처리합니다. 사용자별 동시 처리 한도에 도달하면 순서를 기다립니다.
                            </div>
                        )}
                        {props.actionType === 'cancel_urgent_enqueue' && (
                            <div className="alert alert-info" role="alert">
                                아직 큐에 들어가지 않은 우선 실행 요청을 해제합니다. 이미 큐에 들어갔거나 처리 중인 작업은 계속됩니다.
                            </div>
                        )}
                        {props.actionType === 'try_assign_my_compare_item' && (
                            <div className="alert alert-info" role="alert">
                                선택한 상품 모두에 내 상품 자동 지정 시도를 설정합니다. 다음 상품 처리에서 자동 지정을 시도합니다. 지원하지 않는 비교 URL이 포함되면 적용하지 않습니다.
                            </div>
                        )}
                        {props.actionType === 'instant_process_dry_run' && (
                            <div className="alert alert-info" role="alert">
                                가격을 계산하고 작업 로그에 기록합니다. 상점 가격과 상품의 가격 상태는 변경하지 않습니다.
                            </div>
                        )}

                        {props.actionType === 'delete' && (
                            <div className="alert alert-warning" role="alert">
                                선택한 {props.selectedItems.length}개 상품을 삭제하시겠습니까?
                            </div>
                        )}

                        {props.actionType === 'change_price' && (
                            <div className="mb-3">
                                <label htmlFor="new-price" className="form-label">새 가격</label>
                                <input
                                    id="new-price"
                                    type="number"
                                    className="form-control"
                                    placeholder="가격을 입력하세요"
                                    value={form.newPrice}
                                    onChange={(e) => setForm((prev) => ({ ...prev, newPrice: e.target.value }))}
                                />
                            </div>
                        )}

                        {props.actionType === 'change_discount' && (
                            <div className="mb-3">
                                <label htmlFor="new-discount" className="form-label">새 할인</label>
                                <input
                                    id="new-discount"
                                    type="number"
                                    min="0"
                                    step="1"
                                    className="form-control"
                                    placeholder="할인값을 입력하세요"
                                    onKeyDown={(event) => { if (event.key === "-") event.preventDefault(); }}
                                    value={form.newDiscount}
                                    onChange={(e) => {
                                        if (e.target.value.includes("-")) return;
                                        setForm((prev) => ({ ...prev, newDiscount: e.target.value }));
                                    }}
                                />
                            </div>
                        )}

                        {props.actionType === 'change_group' && (
                            <div className="mb-3">
                                <label htmlFor="item-group" className="form-label">아이템 그룹</label>
                                <select
                                    id="item-group"
                                    className="form-select"
                                    value={form.selectedGroup}
                                    onChange={(e) => setForm((prev) => ({ ...prev, selectedGroup: e.target.value }))}
                                >
                                    <option value="">-- 그룹 선택 --</option>
                                    <option value="none">(그룹 없음)</option>
                                    {props.itemGroups.map(group => (
                                        <option key={group.id} value={group.id}>
                                            {group.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                        {(props.actionType === 'change_rank_price_basis' || props.actionType === 'change_gap_price_basis') && (
                            <div className="mb-3">
                                <label htmlFor="price-basis" className="form-label">
                                    {props.actionType === 'change_rank_price_basis' ? '순위 기준' : '차액 기준'}
                                </label>
                                <select
                                    id="price-basis"
                                    className="form-select"
                                    value={form.priceBasis}
                                    onChange={(e) => setForm((prev) => ({ ...prev, priceBasis: e.target.value }))}
                                >
                                    <option value="">-- 기준 선택 --</option>
                                    <option value="price_exposed">노출가</option>
                                    <option value="price" disabled={props.actionType === 'change_rank_price_basis' && !props.auth.subscriptionStatus?.item_is_premium}>
                                        판매가 {props.actionType === 'change_rank_price_basis' && !props.auth.subscriptionStatus?.item_is_premium && '(프로 전용)'}
                                    </option>
                                </select>
                            </div>
                        )}
                        {props.actionType === 'change_adjust_strategy' && (
                            <div className="mb-3">
                                <label htmlFor="adjust-strategy" className="form-label">가격 조정 방식</label>
                                <select
                                    id="adjust-strategy"
                                    className="form-select"
                                    value={form.adjustStrategy}
                                    onChange={(e) => setForm((prev) => ({ ...prev, adjustStrategy: e.target.value }))}
                                >
                                    <option value="">-- 조정 방식 선택 --</option>
                                    <option value="adjust_price">판매가 조정</option>
                                    <option value="adjust_discount">할인액 조정</option>
                                    <option value="adjust_price_match_discount" disabled={!props.auth.subscriptionStatus?.item_is_premium}>
                                        할인가 맞춤 판매가 조정 {!props.auth.subscriptionStatus?.item_is_premium && '(프로 전용)'}
                                    </option>
                                    <option value="adjust_discount_match_price" disabled={!props.auth.subscriptionStatus?.item_is_premium}>
                                        판매가 맞춤 할인액 조정 {!props.auth.subscriptionStatus?.item_is_premium && '(프로 전용)'}
                                    </option>
                                </select>
                                <div className="form-text">맞춤 조정 방식은 차액 기준이 판매가인 상품에만 적용할 수 있습니다. 롯데ON·SSG닷컴 상품은 판매가 조정만 지원합니다.</div>
                            </div>
                        )}
                    </>
                )}
            </Modal.Body>

            <Modal.Footer>
                {phase === 'done' ? (
                    <button type="button" className="btn btn-primary" onClick={handleConfirmClose}>
                        확인
                    </button>
                ) : (
                    <>
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={handleModalHide}
                            disabled={phase === 'processing'}
                        >
                            취소
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleSubmit}
                            disabled={isSubmitDisabled}
                        >
                            {phase === 'processing' ? "처리중..." : "적용"}
                        </button>
                    </>
                )}
            </Modal.Footer>
        </Modal>
    )
}

export default ItemBatchActionModal;
